import { queryOptions } from '@tanstack/react-query'
import { HTTPError } from 'ky'
import { bff_client } from '@/lib/apiClient'
import type { BaseResponseCollection } from '@/types'
import type { AreaType } from '@/types/area-type'

const AREAS_PER_PAGE = 50
const NOT_FOUND_STATUS = 404

// Áreas raramente mudam: nunca ficam stale nem são descartadas do cache.
const CACHE_FOREVER = {
  staleTime: Number.POSITIVE_INFINITY,
  gcTime: Number.POSITIVE_INFINITY,
} as const

export const areasKeys = {
  all: ['areas'] as const,
  list: () => [...areasKeys.all, 'list'] as const,
  detail: (slug: string) => [...areasKeys.all, 'detail', slug] as const,
}

export const areasQueryOptions = queryOptions({
  queryKey: areasKeys.list(),
  queryFn: async ({ signal }) => {
    const { data }: BaseResponseCollection<AreaType> = await bff_client
      .get(`areas?per_page=${AREAS_PER_PAGE}`, { signal })
      .json()
    return data
  },
  ...CACHE_FOREVER,
})

export function areaQueryOptions(slug: string) {
  return queryOptions({
    queryKey: areasKeys.detail(slug),
    queryFn: ({ signal }) =>
      bff_client
        .get(`areas/${encodeURIComponent(slug)}`, { signal })
        .json<AreaType>(),
    // 404 = slug que não é uma área; não adianta tentar de novo.
    retry: (failureCount, error) =>
      !(
        error instanceof HTTPError && error.response.status === NOT_FOUND_STATUS
      ) && failureCount < 2,
    ...CACHE_FOREVER,
  })
}

const CATEGORIES_PER_PAGE = 100

interface CategorySlugEntry {
  reference: string
  url: string
}

/**
 * Slug real de cada categoria (`reference → url`). A área só devolve nome e
 * referência, e o slug nem sempre é derivável do nome (ex.: "Artes & Teatro"
 * → `artes`).
 */
export const categorySlugsQueryOptions = queryOptions({
  queryKey: ['categories', 'slugs'] as const,
  queryFn: async ({ signal }) => {
    const { data }: BaseResponseCollection<CategorySlugEntry> = await bff_client
      .get(`categories?per_page=${CATEGORIES_PER_PAGE}`, { signal })
      .json()
    return new Map(data.map(category => [category.reference, category.url]))
  },
  ...CACHE_FOREVER,
})
